import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { UNIVERSE_CANCEL_EVENT } from '../hooks/useSafeLoader';

// Pause Universe background rendering while modal overlays are open
export const UNIVERSE_PAUSE_EVENT = 'ul:universe:pause';
export const UNIVERSE_RESUME_EVENT = 'ul:universe:resume';

export default function ModalShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  // If we have a backgroundLocation, this is a real overlay.
  const backgroundLocation = location.state?.backgroundLocation;

  // PERF: When this modal is shown as an overlay, the Universe route stays mounted underneath.
  // Pause canvas animation/rendering to prevent main-thread stalls on mobile.
  useEffect(() => {
    if (!backgroundLocation) return;
    window.dispatchEvent(new Event(UNIVERSE_PAUSE_EVENT));
    return () => {
      window.dispatchEvent(new Event(UNIVERSE_RESUME_EVENT));
    };
  }, [backgroundLocation]);

  const fallbackCloseTo = useMemo(() => {
    // Pick a safe "home base" if user landed directly on modal route
    // (refresh, external link, etc.)
    return '/universe';
  }, []);

  // Memoized close handler - stable reference for event listeners
  const handleClose = useCallback(() => {
    // Kill any "loading" state before leaving
    window.dispatchEvent(
      new CustomEvent(UNIVERSE_CANCEL_EVENT, { detail: { source: 'ModalShell:close' } })
    );

    // If this is a true overlay, go back.
    if (backgroundLocation && window.history.length > 1) {
      navigate(-1);
      return;
    }

    // Otherwise, replace to a safe base route
    navigate(fallbackCloseTo, { replace: true });
  }, [backgroundLocation, fallbackCloseTo, navigate]);

  useEffect(() => {
    // Body scroll lock
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, []);

  // Escape closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  return (
    <div className="fixed inset-0 z-[2147483647]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onPointerDown={handleClose}
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center pointer-events-none">
        <div
          className="relative surface-card w-full md:w-[900px] md:max-w-[95vw] h-[85vh] md:h-[80vh] rounded-t-2xl md:rounded-2xl overflow-hidden shadow-xl pointer-events-auto"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            type="button"
            className="absolute top-3 right-3 z-20 p-2 rounded-full bg-black/10 hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20 transition-colors"
            onPointerDown={(e) => { e.stopPropagation(); handleClose(); }}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>

          {/* Drag Handle (mobile) */}
          <div className="md:hidden absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-300 dark:bg-slate-600 rounded-full" />

          {/* Content */}
          <div className="h-full overflow-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
